import { formatVatType } from "components/Beneficiar/utils";
import { vatOptions } from "components/BeneficiaryFormContainer/ProfileStep";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import FiltersAutocomplete from "components/shared/FiltersAutocomplete";
import InfoBlockBlue from "components/shared/InfoBlockBlue";
import IosSwitch from "components/shared/IosSwitch";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import { findCountry, getCountries } from "countries";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    IBeneficiaryDocumentType,
    TDocumentTypeBeneficiariesCount,
    TDocumentTypeCriteria,
    TDocumentTypePayload,
} from "store/beneficiaryDocumentTypesStore";
import {
    countDocumentTypeBeneficiaries,
    emptyCriteria,
    fetchDocumentTypeCriteriaOptions,
} from "store/beneficiaryDocumentTypesStore";

import styles from "./style.module.scss";

interface IDocumentTypeForm {
    initial: IBeneficiaryDocumentType | null;
    saving: boolean;
    onSubmit: (data: TDocumentTypePayload) => void;
    onCancel: () => void;
}

/** Pseudo country: every residence outside the company country. */
const ABROAD = "abroad";

const DocumentTypeForm = ({ initial, saving, onSubmit, onCancel }: IDocumentTypeForm) => {
    const [t] = useTranslation();
    const { companyInfos, countryFeatures, getCompanySettings, companyCountryCode } = useCompanySettings();

    const [name, setName] = useState(initial?.name ?? "");
    const [requiresValidity, setRequiresValidity] = useState(initial?.requiresValidity ?? true);
    const [criteria, setCriteria] = useState<TDocumentTypeCriteria>({ ...emptyCriteria, ...initial?.criteria });
    const [count, setCount] = useState<TDocumentTypeBeneficiariesCount | null>(null);
    const [beneficiaryCountries, setBeneficiaryCountries] = useState<string[]>([]);
    const debouncedCriteria = useDebounce(criteria, 400);

    // "Expected from N active beneficiaries out of M": refreshed as the criteria change.
    // A failed request keeps the last value shown (the banner is informative only).
    useEffect(() => {
        let cancelled = false;
        countDocumentTypeBeneficiaries(debouncedCriteria)
            .then((result) => !cancelled && setCount(result))
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [debouncedCriteria]);

    // The countries offered are the ones actually set on the beneficiaries, not the whole world
    useEffect(() => {
        let cancelled = false;
        fetchDocumentTypeCriteriaOptions()
            .then((options) => !cancelled && setBeneficiaryCountries(options.countries))
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, []);

    // Same lists and labels as the beneficiary form (ProfileStep), driven by the company country
    const precountDirect = countryFeatures.precountMode === "direct";
    const precountList: FilterOption[] = countryFeatures.precount.options.map((o) => ({
        id: o.value,
        name: t(o.label),
    }));
    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");
    // Stored profiles no longer offered (e.g. 20 % VAT switched off) stay visible so they can be removed
    const vatList = useMemo<FilterOption[]>(() => {
        const offered = vatOptions(countryFeatures.vat.options, usesTwentyPercentVat);
        const stored = (criteria.vatProfiles ?? []).filter((value) => !offered.some((o) => o.value === value));

        return [
            ...offered.map((o) => ({ id: o.value, name: o.label })),
            ...stored.map((value) => ({ id: value, name: formatVatType(value) })),
        ];
    }, [countryFeatures.vat.options, usesTwentyPercentVat, criteria.vatProfiles]);
    // Stored codes outside the publisher-type list (seeded defaults) stay visible so they are not silently dropped
    const socialProfiles = useMemo<FilterOption[]>(() => {
        const offered = socialProfileList[(companyInfos?.distributorType ?? 0) - 1] ?? socialProfileList[0];
        const stored = (criteria.socialProfileTypes ?? []).filter((code) => !offered.some((o) => o.type === code));

        return [
            ...offered.map((o) => ({ id: o.type, name: `${o.type} — ${o.description}` })),
            ...stored.map((type) => ({ id: type, name: type })),
        ];
    }, [companyInfos?.distributorType, criteria.socialProfileTypes]);
    const knownCountries = useMemo(() => getCountries(), []);
    const companyCountryName = findCountry(knownCountries, companyCountryCode)?.name ?? companyCountryCode;
    // "Abroad" first, then the countries set on the beneficiaries (and the stored ones)
    const countryOptions = useMemo<FilterOption[]>(() => {
        const values = new Set([...beneficiaryCountries, ...(criteria.countries ?? [])]);
        const abroad = { id: ABROAD, name: t("BeneficiaryDocuments.Criteria.Abroad", { country: companyCountryName }) };

        return [
            abroad,
            ...[...values].map((value) => {
                const country = findCountry(knownCountries, value);
                return { id: value, name: country ? `${country.flag} ${country.name}` : value };
            }),
        ];
    }, [knownCountries, beneficiaryCountries, criteria.countries, companyCountryName, t]);
    const selectedCountries = criteria.excludeCountries
        ? countryOptions.filter((option) => option.id === ABROAD)
        : countryOptions.filter((option) => criteria.countries?.includes(String(option.id)));
    // Abroad is exclusive: it stands for "outside the company country" and replaces any country list
    const onCountriesChange = (selected: FilterOption[]) => {
        const abroad = selected.some((option) => option.id === ABROAD) && !criteria.excludeCountries;
        const codes = selected.filter((option) => option.id !== ABROAD).map((option) => String(option.id));
        setCriteria((current) => ({
            ...current,
            countries: abroad ? [companyCountryCode] : codes,
            excludeCountries: abroad ? true : null,
        }));
    };

    const setCriterion = <K extends keyof TDocumentTypeCriteria>(key: K, value: TDocumentTypeCriteria[K]) =>
        setCriteria((current) => ({ ...current, [key]: value }));

    const handleSubmit = () => {
        if (!name.trim()) return;
        onSubmit({
            name: name.trim(),
            requiresValidity,
            criteria: {
                ...criteria,
                // Derived from the social profile where the country works that way: never a criterion of its own
                precount: precountDirect ? criteria.precount : null,
                vatProfiles: criteria.vatProfiles?.length ? criteria.vatProfiles : null,
                countries: criteria.countries?.length ? criteria.countries : null,
                excludeCountries: criteria.countries?.length && criteria.excludeCountries ? true : null,
                socialProfileTypes: criteria.socialProfileTypes?.length ? criteria.socialProfileTypes : null,
            },
        });
    };

    // Precount as on the beneficiary form: a yes/no choice where the country asks it directly,
    // the URSSAF social profile (which the precount derives from) elsewhere. A social-profile
    // criterion stored on a direct-mode company (country change) stays visible so it can be cleared.
    const showPrecountSelect = precountDirect && (precountList.length > 0 || criteria.precount !== null);
    const showSocialProfiles = !precountDirect || !!criteria.socialProfileTypes?.length;
    const anyPlaceholder = t("BeneficiaryDocuments.Criteria.Any");
    const entityOptions: FilterOption[] = [
        { id: "physical", name: t("LegalEntity.Physical") },
        { id: "legal", name: t("LegalEntity.Legal") },
    ];
    // A stored precount value the country does not offer (country change) is still shown so it can be cleared
    const precountOptions =
        criteria.precount !== null && !precountList.some((o) => o.id === criteria.precount)
            ? [...precountList, { id: criteria.precount, name: String(criteria.precount) }]
            : precountList;

    return (
        <div className={styles.form}>
            <div className={styles.field}>
                <label>{t("BeneficiaryDocuments.Form.Name")}</label>
                <StyledTextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("BeneficiaryDocuments.Form.NamePlaceholder")}
                    autoFocus
                />
            </div>

            <div className={styles.switchRow}>
                <div className={styles.switchText}>
                    <span>{t("BeneficiaryDocuments.Form.RequiresValidity")}</span>
                    <span>{t("BeneficiaryDocuments.Form.RequiresValidityHelp")}</span>
                </div>
                <IosSwitch checked={requiresValidity} onChange={(e) => setRequiresValidity(e.target.checked)} />
            </div>

            <div className={styles.criteriaTitle}>{t("BeneficiaryDocuments.Form.CriteriaTitle")}</div>
            <span className={styles.muted}>{t("BeneficiaryDocuments.Form.CriteriaHelp")}</span>

            <div className={styles.criteriaGrid}>
                {showPrecountSelect && (
                    <FiltersAutocomplete
                        label={t("BeneficiaryDocuments.Criteria.Precount")}
                        placeholder={anyPlaceholder}
                        filtersOptions={precountOptions}
                        chosenOptions={precountOptions.filter((o) => o.id === criteria.precount)}
                        handleChooseOption={(selected: FilterOption[]) =>
                            setCriterion("precount", selected.length ? Number(selected.at(-1)?.id) : null)
                        }
                        optionsLimit={1}
                    />
                )}
                {showSocialProfiles && (
                    <div style={{ gridColumn: "1 / -1" }}>
                        <FiltersAutocomplete
                            label={t(
                                precountDirect
                                    ? "BeneficiaryDocuments.Criteria.SocialProfile"
                                    : "BeneficiaryDocuments.Criteria.Precount",
                            )}
                            placeholder={anyPlaceholder}
                            filtersOptions={socialProfiles}
                            chosenOptions={socialProfiles.filter(
                                (o) => criteria.socialProfileTypes?.includes(String(o.id)),
                            )}
                            handleChooseOption={(selected: FilterOption[]) =>
                                setCriterion(
                                    "socialProfileTypes",
                                    selected.map((o) => String(o.id)),
                                )
                            }
                            disableCloseOnSelect
                        />
                    </div>
                )}
                {vatList.length > 0 && (
                    <FiltersAutocomplete
                        label={t("BeneficiaryDocuments.Criteria.Vat")}
                        placeholder={anyPlaceholder}
                        filtersOptions={vatList}
                        chosenOptions={vatList.filter((o) => criteria.vatProfiles?.includes(Number(o.id)))}
                        handleChooseOption={(selected: FilterOption[]) =>
                            setCriterion(
                                "vatProfiles",
                                selected.map((o) => Number(o.id)),
                            )
                        }
                        disableCloseOnSelect
                    />
                )}
                <FiltersAutocomplete
                    label={t("BeneficiaryDocuments.Criteria.Entity")}
                    placeholder={anyPlaceholder}
                    filtersOptions={entityOptions}
                    chosenOptions={entityOptions.filter((o) => o.id === criteria.entity)}
                    handleChooseOption={(selected: FilterOption[]) =>
                        setCriterion("entity", (selected.at(-1)?.id as "legal" | "physical" | undefined) ?? null)
                    }
                    optionsLimit={1}
                />
                <FiltersAutocomplete
                    label={t("BeneficiaryDocuments.Criteria.Countries")}
                    placeholder={anyPlaceholder}
                    filtersOptions={countryOptions}
                    chosenOptions={selectedCountries}
                    handleChooseOption={onCountriesChange}
                    disableCloseOnSelect
                />
            </div>

            {count && (
                <InfoBlockBlue
                    icon={<span className={styles.countDot} />}
                    info={t("BeneficiaryDocuments.Form.MatchingCount", {
                        matching: count.matching,
                        total: count.total,
                    })}
                />
            )}

            {/* Same footer as the other confirmation modals (DeleteModal): two full-width buttons */}
            <div className={styles.buttons}>
                <StyledButton buttonType="secondary" fullWidth onClick={onCancel}>
                    {t("BeneficiaryDocuments.Form.Cancel")}
                </StyledButton>
                <StyledButton
                    buttonType="primary"
                    fullWidth
                    onClick={handleSubmit}
                    loading={saving}
                    disabled={!name.trim()}
                >
                    {initial ? t("BeneficiaryDocuments.Form.Save") : t("BeneficiaryDocuments.Form.Create")}
                </StyledButton>
            </div>
        </div>
    );
};

export default DocumentTypeForm;
